import { useCallback } from 'react';

import { FieldSuggestionOption } from '../InputGroupWithSuggestions';

type UseOnKeyDownProps = {
  isOpen: boolean;
  menuFocus: number;
  suggestions: FieldSuggestionOption[];
  defaultOptionContentValue: string;
  handleClose: (submitValue?: string | null) => void;
  handleOpen: () => void;
  setMenuFocus: React.Dispatch<React.SetStateAction<number>>;
  setLocalValue: (value: string | null) => void;
};

export const useOnKeyDown = ({
  isOpen,
  menuFocus,
  suggestions,
  defaultOptionContentValue,
  handleClose,
  handleOpen,
  setMenuFocus,
  setLocalValue,
}: UseOnKeyDownProps) =>
  useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const keyMap: Record<
        string,
        (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
      > = {
        Tab: () => {
          isOpen && handleClose();
        },
        Escape: () => {
          isOpen && handleClose();
        },
        ArrowDown: (
          e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
        ) => {
          // If Menu is open, focus next MenuItem or loop to beginning (uses <= suggestions.length because of default value)
          if (isOpen) {
            // prevent default to remove effect on input/textarea
            e.preventDefault();
            setMenuFocus((ind) =>
              ind + 1 <= suggestions.length ? ind + 1 : 0,
            );
          } else {
            setMenuFocus(0);
            handleOpen();
          }
        },
        ArrowUp: (
          e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
        ) => {
          // If Menu is open, focus prev MenuItem or loop to end (suggestions.length because of default value)
          if (isOpen) {
            // prevent default to remove effect on input/textarea
            e.preventDefault();
            setMenuFocus((ind) =>
              ind - 1 >= 0 ? ind - 1 : suggestions.length,
            );
          } else {
            setMenuFocus(suggestions.length);
            handleOpen();
          }
        },
        Enter: (
          e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
        ) => {
          if (e.shiftKey) {
            return;
          }
          e.preventDefault();
          if (isOpen) {
            let submitValue = null;
            if (menuFocus === suggestions.length) {
              submitValue = defaultOptionContentValue;
            } else if (menuFocus >= 0 && menuFocus < suggestions.length) {
              submitValue = suggestions[menuFocus].value;
            }
            setLocalValue(submitValue);
            handleClose(submitValue);
          } else {
            setMenuFocus(0);
            handleOpen();
          }
        },
      };

      keyMap[e.key]?.(e);
    },
    [
      isOpen,
      menuFocus,
      suggestions,
      defaultOptionContentValue,
      handleClose,
      handleOpen,
      setLocalValue,
      setMenuFocus,
    ],
  );
